import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row, Input, Form, Label, Spinner, Button } from "reactstrap";
import { updateProfileEmployer } from "../../../../actions/customer";
import {
  authKey_local_storage_key,
  userInfo_local_storage_key,
} from "../../../../helpers/common";
import ProfileImageUploader from "../../../../helpers/Common/ProfileImageUploader";
import { CustomSnackbar } from "../../../../helpers/CustomSnackbar";
import ChangeLoginFieldModal from "../../../../helpers/Common/ChangeLoginFieldModal";
import { UPDATE_PROFILE_SUCCESS } from "../../../../constants/actionTypes";
import { ResendverifyEmailLink } from "../../../../api/customer"; // Adjust path as needed

const UpdateProfile = () => {
  const { profile } = useSelector((state) => state.customer);
  const [loading, setLoading] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const dispatch = useDispatch();
  const token = localStorage.getItem(authKey_local_storage_key);

  console.log("🔍 [UpdateProfile Mount/Render] Redux Profile State:", profile);

  const [openChangeEmailModal, setOpenChangeEmailModal] = useState(false);
  const [openChangeMobileModal, setOpenChangeMobileModal] = useState(false);

  // Helper to check if email is verified directly from Redux state
  const checkEmailVerification = () => {
    if (profile?.data?.isEmailVerfied !== undefined && profile?.data?.isEmailVerfied !== null) {
      return Boolean(profile.data.isEmailVerfied);
    }
    if (profile?.data?.isEmailVerified !== undefined && profile?.data?.isEmailVerified !== null) {
      return Boolean(profile.data.isEmailVerified);
    }
    if (profile?.isEmailVerfied !== undefined) return Boolean(profile.isEmailVerfied);
    if (profile?.isEmailVerified !== undefined) return Boolean(profile.isEmailVerified);
    return false;
  };

  const isEmailVerified = checkEmailVerification();

  const handleResendVerification = async () => {
    const targetEmail = formData.email || profile?.data?.email || profile?.email;
    if (!targetEmail) {
      CustomSnackbar.error("Email address not found.");
      return;
    }

    setResendingEmail(true);
    try {
      const response = await ResendverifyEmailLink({ email: targetEmail });
      if (response?.status === 200 || response?.data?.status || response?.data?.success) {
        CustomSnackbar.success("Verification link sent! Check your email inbox.");
      } else {
        CustomSnackbar.error(response?.data?.message || "Failed to send link.");
      }
    } catch (err) {
      CustomSnackbar.error("Error sending verification email.");
    } finally {
      setResendingEmail(false);
    }
  };

  // Safe Extraction helper to avoid parsing crashes during rendering steps
  const getParsedFields = (fieldData) => {
    if (!fieldData) return {};
    if (typeof fieldData === "object") return fieldData;
    try {
      return JSON.parse(fieldData);
    } catch (e) {
      console.error(" Failed to parse additional_fields:", e);
      return {};
    }
  };

  const getStoredCustomer = () => {
    try {
      const raw = localStorage.getItem(userInfo_local_storage_key);
      console.log(
        "📥 [getStoredCustomer] Raw data pulled from localStorage:",
        raw,
      );
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.customer || null;
    } catch (e) {
      console.error("🛑 Failed to parse stored customer:", e);
      return null;
    }
  };

  const resolveProfileImage = (reduxProfile) => {
    const stored = getStoredCustomer();

    console.log("========== IMAGE TRACE ==========");
    console.log("Stored customer:", stored);
    console.log("Redux profile:", reduxProfile);

    console.log("Stored data.profileImage:", stored?.data?.profileImage);

    console.log("Stored profileImage:", stored?.profileImage);

    console.log("Stored additional_fields:", stored?.additional_fields);

    const parsedFields = getParsedFields(
      stored?.additional_fields || reduxProfile?.additional_fields,
    );

    console.log("Parsed profile_picture:", parsedFields?.profile_picture);

    const resolved =
      stored?.data?.profileImage ||
      stored?.profileImage ||
      reduxProfile?.data?.profileImage ||
      reduxProfile?.profileImage ||
      parsedFields?.profile_picture ||
      "";

    console.log("FINAL RESOLVED:", resolved);
    console.log("===============================");

    return resolved;
  };

  const buildFormDataFromProfile = (p) => ({
    name: p?.data?.username || p?.username || "",
    email: p?.data?.email || "",
    gender: p?.data?.gender || "",
    address: p?.data?.address || p?.address || "",
    mobile_number: p?.mobile_number || "",
    username: p?.username || "",
    additional_fields: getParsedFields(p?.additional_fields),
  });

  const [formData, setFormData] = useState(() =>
    buildFormDataFromProfile(profile),
  );
  const [currentImageName, setCurrentImageName] = useState(() =>
    resolveProfileImage(profile),
  );

  // Keep form fields in sync when Redux profile changes
  useEffect(() => {
    if (profile) {
      console.log(
        "🔄 [useEffect - profile changed] Syncing form fields with Redux baseline profile profile.",
      );
      setFormData(buildFormDataFromProfile(profile));
    }
  }, [profile]);

  useEffect(() => {
    if (!profile) return;

    const resolved = resolveProfileImage(profile);

    console.log("Resolved Image:", resolved);
    console.log("Current:", currentImageName);

    if (resolved && resolved !== currentImageName) {
      setCurrentImageName(resolved);
    }
  }, [profile]);

  const onChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const onNumberInputChange = (e) => {
    const inputValue = e.target.value;
    if (!inputValue) {
      setFormData({ ...formData, [e.target.name]: "" });
      return;
    }
    if (!/^\d+$/.test(inputValue)) return;
    setFormData({ ...formData, [e.target.name]: parseInt(inputValue, 10) });
  };

  const handleChangeEmail = () => setOpenChangeEmailModal(true);
  const handleChangeMobile = () => setOpenChangeMobileModal(true);

  const handleEmailChangeSuccess = (newEmail) => {
    setFormData((prev) => ({ ...prev, email: newEmail }));
    CustomSnackbar.success("Email updated successfully!");
  };

  const handleMobileChangeSuccess = (newMobile) => {
    setFormData((prev) => ({ ...prev, mobile_number: newMobile }));
    CustomSnackbar.success("Mobile number updated successfully!");
  };

  const updateProfileHandle = (newProfilePic) => {
    setLoading(true);

    console.log("🛰️ [updateProfileHandle] Running structure sync execution.");

    const currentFields = getParsedFields(formData?.additional_fields);
    const updatedAdditionalFields = newProfilePic
      ? { ...currentFields, profile_picture: newProfilePic }
      : currentFields;

    const stringifiedFields = JSON.stringify(updatedAdditionalFields);

    const payload = {
      name: formData.name,
      email: formData.email,
      gender: formData.gender,
      address: formData.address,
      mobile_number: formData.mobile_number,
      username: formData.username,
      additional_fields: stringifiedFields,
    };

    dispatch(updateProfileEmployer({ ...payload, id: profile?.id }))
      .then((res) => {
        setLoading(false);

        const actualData = res?.data || res;
        console.log("📦 [API Response Received] Raw payload:", actualData);
        console.log("=========== UPDATE RESPONSE ===========");
        console.log(res);
        console.log("=======================================");

        if (actualData) {
          CustomSnackbar.success("Profile updated successfully.");

          const serverFields = actualData?.data ? actualData.data : actualData;

          const flatSyncObject = {
            id: serverFields?.userId || profile?.id || 107,
            name: formData.name || serverFields?.username || profile?.name,
            email: formData.email || serverFields?.email || profile?.email,
            mobile_number:
              formData.mobile_number ||
              serverFields?.phoneNumber ||
              profile?.mobile_number,
            customerType:
              serverFields?.customerType !== undefined
                ? serverFields.customerType
                : profile?.customerType || 0,
            additional_fields: stringifiedFields,
          };

          console.log(
            "💾 [Storage Save Fix] Writing perfectly flat structure back to session:",
            flatSyncObject,
          );

          localStorage.setItem(
            userInfo_local_storage_key,
            JSON.stringify(flatSyncObject),
          );

          console.log("activeImage:", {
            newProfilePic,
            currentImageName,
            profilePicture: updatedAdditionalFields?.profile_picture,
          });
          const activeImage =
            newProfilePic ||
            currentImageName ||
            updatedAdditionalFields?.profile_picture;
          if (activeImage) {
            setCurrentImageName(activeImage);
          }

          setFormData((prev) => ({
            ...prev,
            additional_fields: updatedAdditionalFields,
          }));
        }
      })

      .catch((err) => {
        setLoading(false);
        console.error("🛑 [API Error] Profile update failed:", err);
        CustomSnackbar.error("Failed to update profile settings.");
      });
  };

  const handleImageUploadSuccess = (newProfilePicName) => {
    console.log("📸 Image uploaded successfully:", newProfilePicName);

    setCurrentImageName(newProfilePicName);

    const currentFields = getParsedFields(formData?.additional_fields);
    const updatedAdditionalFields = {
      ...currentFields,
      profile_picture: newProfilePicName,
    };
    const stringifiedFields = JSON.stringify(updatedAdditionalFields);

    const updatedProfileData = {
      ...profile,
      id: profile?.id,
      name: formData.name || profile?.name,
      email: formData.email || profile?.email,
      mobile_number: formData.mobile_number || profile?.mobile_number,
      customerType: profile?.customerType,
      role: profile?.role,
      roles: profile?.roles,
      
      profileImage: newProfilePicName, 
      data: {
        ...(profile?.data || {}),
        profileImage: newProfilePicName,
      },
      additional_fields: stringifiedFields,
    };

    const storageWrapperObject = {
      customer: {
        ...updatedProfileData
      }
    };

    setFormData((prev) => ({
      ...prev,
      additional_fields: updatedAdditionalFields,
    }));

    const imageUrl = `https://sdsjobseekers.runasp.net/uploads/profile_image/${newProfilePicName}`;
    localStorage.setItem("temp_uploaded_avatar_url", imageUrl);

    localStorage.setItem(
      userInfo_local_storage_key,
      JSON.stringify(storageWrapperObject)
    );

    dispatch({
      type: UPDATE_PROFILE_SUCCESS,
      payload: updatedProfileData,
    });

    window.dispatchEvent(new Event("instant_navbar_refresh"));
    console.log("✅ State sync complete and layout updates dispatched.");
  };

  const onSubmit = (e) => {
    e.preventDefault();
    console.log(
      "🚀 [Form Submitted] Form submission event captured. Current state tracking parameters:",
      formData,
    );

    if (!formData.name) {
      console.warn(
        "⚠️ [Validation Error] Form submit blocked. Name field is missing value properties.",
      );
      CustomSnackbar.error("Please enter your name");
      return;
    }

    updateProfileHandle();
  };

  console.log("Passing to uploader:", currentImageName);

  return (
    <>
      <Form onSubmit={onSubmit}>
        <ProfileImageUploader
          initialImageName={currentImageName}
          onUploadSuccess={handleImageUploadSuccess}
          uploadPath="/CandidateAttachment"
        />

        <Row>
          <Col lg={12}>
            <div className="mb-3">
              <label htmlFor="fullName" className="form-label">
                Full Name*
              </label>
              <Input
                name="name"
                value={formData.name}
                type="text"
                className="form-control"
                onChange={onChange}
                id="fullName"
                placeholder="Enter your Full name"
              />
            </div>
          </Col>

          <Col lg={6}>
            <div className="mb-3">
              <Label htmlFor="mobile_number" className="form-label">
                Mobile Number *
              </Label>
              <div className="d-flex">
                <Input
                  name="mobile_number"
                  type="text"
                  value={formData.mobile_number}
                  className="form-control"
                  onChange={onNumberInputChange}
                  id="mobile_number"
                  maxLength={10}
                  placeholder="Enter your mobile number"
                  disabled
                />
                <Button
                  color="outline-primary"
                  size="sm"
                  className="profile-edit-btn"
                  onClick={handleChangeMobile}
                  title="Change Mobile Number"
                >
                  <i className="uil uil-pen"></i>
                </Button>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className="mb-3">
              <Label htmlFor="email" className="form-label">
                Email *
              </Label>
              <div className="d-flex">
                <Input
                  name="email"
                  type="text"
                  value={formData.email}
                  className="form-control"
                  onChange={onChange}
                  id="email"
                  placeholder="Enter your email address"
                  disabled
                />
                <Button
                  color="outline-primary"
                  size="sm"
                  className="profile-edit-btn"
                  onClick={handleChangeEmail}
                  title="Change Email"
                >
                  <i className="uil uil-pen"></i>
                </Button>
              </div>

              {/* Status text directly below the email input field */}
              <div className="mt-1 fs-12">
                {isEmailVerified ? (
                  <span className="text-success fw-medium">
                    <i className="uil uil-check-circle me-1"></i>Email Verified
                  </span>
                ) : (
                  <div className="d-flex align-items-center gap-1">
                    <span className="text-danger fw-medium">
                      <i className="uil uil-exclamation-triangle me-1"></i>Unverified Email.
                    </span>
                    <Button
                      color="link"
                      className="p-0 text-decoration-underline text-primary fs-12 border-0 bg-transparent fw-medium ms-1"
                      onClick={handleResendVerification}
                      disabled={resendingEmail}
                      type="button"
                    >
                      {resendingEmail ? (
                        <>
                          <Spinner size="sm" style={{ width: "10px", height: "10px" }} /> Sending...
                        </>
                      ) : (
                        "Verify Email"
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className="mb-3">
              <Label htmlFor="gender" className="form-label">
                Gender
              </Label>
              <Input
                type="select"
                name="gender"
                id="gender"
                value={formData.gender || ""}
                onChange={onChange}
                className="form-control"
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </Input>
            </div>
          </Col>

          <Col lg={6}>
            <div className="mb-3">
              <Label htmlFor="address" className="form-label">
                Address *
              </Label>
              <div className="d-flex">
                <Input
                  name="address"
                  type="text"
                  value={formData.address}
                  className="form-control"
                  onChange={onChange}
                  id="address"
                  placeholder="Enter your Address (Optional)"
                />
              </div>
            </div>
          </Col>
        </Row>

        <div className="mt-4 text-end">
          <Button
            type="submit"
            color="primary"
            className="w-100"
            disabled={loading}
          >
            {loading ? (
              <>
                Update <Spinner size="sm" />
              </>
            ) : (
              "Update"
            )}
          </Button>
        </div>
      </Form>

      <ChangeLoginFieldModal
        openModal={openChangeEmailModal}
        setOpenModal={setOpenChangeEmailModal}
        field="email"
        currentValue={formData.email}
        onSuccess={handleEmailChangeSuccess}
      />

      <ChangeLoginFieldModal
        openModal={openChangeMobileModal}
        setOpenModal={setOpenChangeMobileModal}
        field="mobile_number"
        currentValue={formData.mobile_number}
        onSuccess={handleMobileChangeSuccess}
      />
    </>
  );
};

export default UpdateProfile;